import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { castingCallTemplates } from "@/lib/castingCallTemplates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RichTextEditor from "./RichTextEditor";
import CampaignAnalytics from "./CampaignAnalytics";
import CampaignInsights from "./CampaignInsights";

const PREVIEW_SAMPLE = { first_name: "Jax", last_name: "Robins", email: "jax@example.com" };

function renderMergeTags(text, contact) {
    return (text || "")
        .replace(/\{\{first_name\}\}/g, (contact.first_name || "friend").trim() || "friend")
        .replace(/\{\{last_name\}\}/g, contact.last_name || "")
        .replace(/\{\{email\}\}/g, contact.email || "")
        .replace(/\{\{unsubscribe_url\}\}/g, "#preview-unsubscribe");
}

function buildPreviewHtml(bodyHtml, contact) {
    const merged = renderMergeTags(bodyHtml, contact);
    const footer = `
    <hr style="margin-top:40px;border:none;border-top:1px solid #333">
    <p style="color:#8b8478;font-size:12px;line-height:1.6;margin-top:20px;text-align:center">
      You're receiving this because you attended a Mrs.Black(In)Love event and opted in to marketing.<br/>
      Mrs.Black(In)Love · Atlanta, GA<br/>
      <a href="#preview-unsubscribe" style="color:#c9a24a;text-decoration:underline">Unsubscribe with one click</a>
    </p>`;
    // Sanitize before rendering — the composed HTML lives inside an authed-only preview
    // and the admin authored it themselves, but sanitizing is cheap defense-in-depth against
    // XSS if a stray <script> or event handler ever slips in via a merge tag / imported field.
    return DOMPurify.sanitize(merged + footer, {
        ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel|#):/i,
    });
}

// "2026-08-15T10:00" (from datetime-local input, assumed in the user's browser TZ) → ISO with offset.
function localInputToIso(local) {
    if (!local) return "";
    const d = new Date(local); // browser parses as local time
    return d.toISOString();
}

function isoToLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatScheduled(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
}

export default function CampaignsPanel() {
    const [campaigns, setCampaigns] = useState([]);
    const [showCompose, setShowCompose] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [form, setForm] = useState({ name: "", subject: "", subject_b: "", body_html: "", filter_tags: [] });
    const [abTest, setAbTest] = useState(false);
    const [allTags, setAllTags] = useState([]);
    const [audience, setAudience] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [scheduleAt, setScheduleAt] = useState("");
    const [scheduling, setScheduling] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [c, t] = await Promise.all([
                api.get("/admin/campaigns"),
                api.get("/admin/contacts/tags"),
            ]);
            setCampaigns(c.data);
            setAllTags(t.data);
        } catch { toast.error("Could not load campaigns."); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const create = async () => {
        if (!form.name || !form.subject || !form.body_html) {
            toast.error("Name, subject, and body are all required.");
            return;
        }
        if (abTest && !form.subject_b.trim()) {
            toast.error("A/B test needs a Variant B subject line.");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, consent_required: true };
            if (!abTest) payload.subject_b = null;
            const { data } = await api.post("/admin/campaigns", payload);
            toast.success(abTest ? "A/B draft saved." : "Draft saved.");
            setSelectedId(data.id);
            setShowCompose(false);
            setForm({ name: "", subject: "", subject_b: "", body_html: "", filter_tags: [] });
            setAbTest(false);
            refresh();
            const ac = await api.get(`/admin/campaigns/${data.id}/audience-count`);
            setAudience(ac.data.count);
        } catch { toast.error("Save failed."); }
        finally { setBusy(false); }
    };

    const sendTest = async (id) => {
        if (!/^\S+@\S+\.\S+$/.test(testEmail)) {
            toast.error("Enter a valid test email address.");
            return;
        }
        try {
            await api.post(`/admin/campaigns/${id}/send-test`, { to: testEmail });
            toast.success(`Test sent to ${testEmail}.`);
        } catch { toast.error("Test send failed."); }
    };

    const sendReal = async (id, count) => {
        if (!window.confirm(`Send this campaign to ${count} opted-in contacts? This can't be undone.`)) return;
        try {
            await api.post(`/admin/campaigns/${id}/send`);
            toast.success(`Sending to ${count} contacts in the background — refresh in a minute for status.`, { duration: 6000 });
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed.");
        }
    };

    const scheduleSend = async (id, count) => {
        if (!scheduleAt) { toast.error("Pick a date + time first."); return; }
        const iso = localInputToIso(scheduleAt);
        const when = new Date(iso);
        if (when.getTime() < Date.now() + 60_000) {
            toast.error("Schedule at least a minute in the future.");
            return;
        }
        if (!window.confirm(`Schedule this campaign to send to ${count} opted-in contacts on ${when.toLocaleString()}?`)) return;
        setScheduling(true);
        try {
            await api.post(`/admin/campaigns/${id}/schedule`, { scheduled_for: iso });
            toast.success(`Scheduled for ${when.toLocaleString()}.`);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Schedule failed.");
        } finally { setScheduling(false); }
    };

    const unschedule = async (id) => {
        if (!window.confirm("Cancel the scheduled send and move back to draft?")) return;
        try {
            await api.post(`/admin/campaigns/${id}/unschedule`);
            toast.success("Unscheduled — back to draft.");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Unschedule failed.");
        }
    };

    const duplicateCampaign = async (id) => {
        try {
            const { data } = await api.post(`/admin/campaigns/${id}/duplicate`);
            toast.success(`Cloned to "${data.name}" — open it to edit and send.`);
            await refresh();
            setSelectedId(data.id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Duplicate failed.");
        }
    };

    const testToSelf = async (id, name) => {
        try {
            const { data } = await api.post(`/admin/campaigns/${id}/send-test-to-self`);
            toast.success(`"${name}" test sent to ${data.to}. Check your inbox in ~15s.`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Test send failed.");
        }
    };

    const selected = campaigns.find((c) => c.id === selectedId);
    useEffect(() => {
        if (!selected) { setAudience(null); setScheduleAt(""); return; }
        api.get(`/admin/campaigns/${selected.id}/audience-count`).then((r) => setAudience(r.data.count)).catch(() => setAudience(null));
        setScheduleAt(selected.scheduled_for ? isoToLocalInput(selected.scheduled_for) : "");
    }, [selected]);

    return (
        <div data-testid="campaigns-panel">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                    <p className="eyebrow">Email Campaigns</p>
                    <p className="text-sm text-[color:var(--cream-muted)] mt-2 max-w-2xl">
                        Send warm, compliant emails to your opted-in contacts. Merge tags <code className="text-[color:var(--gold)]">{"{{first_name}}"}</code> · <code className="text-[color:var(--gold)]">{"{{last_name}}"}</code> supported in subject and body. An unsubscribe footer is auto-appended and one-click.
                    </p>
                </div>
                <button onClick={() => setShowCompose(true)} className="btn-gold btn-gold-solid" data-testid="campaigns-new">New campaign</button>
            </div>

            <CampaignInsights onStartTest={(seed) => {
                setForm({
                    name: seed.name || "",
                    subject: seed.subject || "",
                    subject_b: seed.subject_b || "",
                    body_html: "",
                    filter_tags: [],
                });
                setAbTest(true);
                setShowCompose(true);
                setSelectedId(null);
            }} />

            {showCompose && (
                <div className="border border-[color:var(--gold)] p-6 mb-8" data-testid="campaigns-compose">
                    <div className="flex justify-between items-baseline mb-4">
                        <h3 className="font-serif text-2xl">Compose</h3>
                        <button onClick={() => setShowCompose(false)} className="link-underline text-xs">Cancel</button>
                    </div>
                    <div className="mb-5 border border-[color:var(--espresso)] p-4" data-testid="campaign-templates">
                        <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-2">Open Casting Call — pre-built templates</p>
                        <p className="text-xs text-[color:var(--cream-muted)] mb-3">Load a warm/urgent/deadline tone into subject + body. Same voice as the draft-nudge emails, aimed at your full contact list.</p>
                        <div className="flex flex-wrap gap-2">
                            {castingCallTemplates().map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => {
                                        if (form.subject || form.body_html) {
                                            if (!window.confirm("This will replace the current subject and body. Continue?")) return;
                                        }
                                        setForm((f) => ({ ...f, ...t.template }));
                                        toast.success(`Loaded "${t.label}".`);
                                    }}
                                    title={t.description}
                                    className="text-xs px-3 py-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-[color:var(--warm-black)] transition-colors"
                                    data-testid={`campaign-template-${t.key}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <input className="input-line w-full mb-3" placeholder="Internal name (only you see this)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name" />
                    <input className="input-line w-full mb-3" placeholder='Subject line (supports {{first_name}})' value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="campaign-subject" />
                    <div className="mb-3 border border-[color:var(--espresso)] p-3" data-testid="campaign-ab-block">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={abTest} onChange={(e) => setAbTest(e.target.checked)} className="accent-[color:var(--gold)]" data-testid="campaign-ab-toggle" />
                            <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">A/B test the subject line</span>
                        </label>
                        {abTest ? (
                            <>
                                <p className="text-[0.6rem] text-[color:var(--cream-muted)] mt-2">Subject A goes to a random 10%. Subject B goes to another random 10%. Four hours later the higher-open-rate winner auto-sends to the remaining 80%.</p>
                                <input
                                    className="input-line w-full mt-3"
                                    placeholder="Variant B subject line (same body, different hook)"
                                    value={form.subject_b}
                                    onChange={(e) => setForm({ ...form, subject_b: e.target.value })}
                                    data-testid="campaign-subject-b"
                                />
                            </>
                        ) : (
                            <p className="text-[0.6rem] text-[color:var(--cream-muted)] mt-2">Send one subject to your whole opted-in list. Toggle on to A/B test two subject variants.</p>
                        )}
                    </div>
                    <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Body · merge tags <code className="text-[color:var(--gold)]">{"{{first_name}}"}</code>, <code className="text-[color:var(--gold)]">{"{{last_name}}"}</code>, <code className="text-[color:var(--gold)]">{"{{email}}"}</code>, <code className="text-[color:var(--gold)]">{"{{unsubscribe_url}}"}</code> supported. Toggle the code icon on the right for raw HTML.</p>
                    <RichTextEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} testid="campaign-body" />
                    {allTags.length > 0 && (
                        <div className="mb-3">
                            <label className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Send only to contacts tagged (optional — leave empty to send to everyone opted in)</label>
                            <div className="flex flex-wrap gap-2 mt-2" data-testid="campaign-tag-filter">
                                {allTags.map((t) => {
                                    const active = form.filter_tags.includes(t.tag);
                                    return (
                                        <button
                                            key={t.tag}
                                            type="button"
                                            onClick={() => setForm({
                                                ...form,
                                                filter_tags: active
                                                    ? form.filter_tags.filter((x) => x !== t.tag)
                                                    : [...form.filter_tags, t.tag],
                                            })}
                                            className={`text-xs px-3 py-1 border ${active ? "bg-[color:var(--gold)] text-[color:var(--warm-black)] border-[color:var(--gold)]" : "border-[color:var(--espresso)] text-[color:var(--cream-muted)]"}`}
                                            data-testid={`campaign-tag-${t.tag}`}
                                        >
                                            {t.tag} · {t.count}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                        <button onClick={create} disabled={busy} className="btn-gold btn-gold-solid disabled:opacity-40" data-testid="campaign-save-draft">{busy ? "Saving…" : "Save draft"}</button>
                        <button
                            type="button"
                            onClick={() => {
                                if (!form.subject && !form.body_html) {
                                    toast.error("Nothing to preview — add a subject or body first.");
                                    return;
                                }
                                setShowPreview(true);
                            }}
                            className="btn-gold text-xs"
                            data-testid="campaign-preview"
                        >
                            Preview email →
                        </button>
                        <span className="text-xs text-[color:var(--cream-muted)]">Save first, then test, then send.</span>
                    </div>
                </div>
            )}

            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-3xl bg-[color:var(--warm-black)] border-[color:var(--gold)] max-h-[90vh] overflow-y-auto" data-testid="campaign-preview-modal">
                    <DialogHeader>
                        <p className="eyebrow">Email preview</p>
                        <DialogTitle className="font-serif text-2xl text-[color:var(--soft-cream)]">Rendered with sample contact</DialogTitle>
                        <p className="text-xs text-[color:var(--cream-muted)] mt-2">
                            Sample: <span className="text-[color:var(--gold)]">{PREVIEW_SAMPLE.first_name} {PREVIEW_SAMPLE.last_name}</span> · {PREVIEW_SAMPLE.email}. Merge tags <code className="text-[color:var(--gold)]">{"{{first_name}}"}</code>, <code className="text-[color:var(--gold)]">{"{{last_name}}"}</code>, <code className="text-[color:var(--gold)]">{"{{email}}"}</code>, <code className="text-[color:var(--gold)]">{"{{unsubscribe_url}}"}</code> are substituted. The unsubscribe footer below is auto-appended to every live send.
                        </p>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                        <div className="border border-[color:var(--espresso)] p-4">
                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Subject</p>
                            <p className="mt-2 font-serif text-lg text-[color:var(--soft-cream)]" data-testid="campaign-preview-subject">{renderMergeTags(form.subject, PREVIEW_SAMPLE) || "(no subject)"}</p>
                        </div>
                        <div className="border border-[color:var(--espresso)]">
                            <div className="bg-[color:var(--espresso)]/40 px-4 py-2 text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Body · rendered</div>
                            <div
                                className="p-2 bg-white text-black"
                                data-testid="campaign-preview-body"
                                dangerouslySetInnerHTML={{ __html: buildPreviewHtml(form.body_html, PREVIEW_SAMPLE) || "<p style='padding:24px;color:#666'>(empty body)</p>" }}
                            />
                        </div>
                    </div>
                    <DialogFooter className="mt-2">
                        <button onClick={() => setShowPreview(false)} className="btn-gold text-xs" data-testid="campaign-preview-close">Close preview</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {selected && (
                <div className="border border-[color:var(--gold)] p-6 mb-8" data-testid="campaign-selected">
                    <div className="flex justify-between items-baseline">
                        <div>
                            <p className="eyebrow">Draft</p>
                            <h3 className="font-serif text-2xl mt-1">{selected.name}</h3>
                            <p className="text-xs text-[color:var(--cream-muted)] mt-1">Subject: {selected.subject}</p>
                            {selected.subject_b && (
                                <p className="text-xs text-[color:var(--cream-muted)] mt-1">A/B · Variant B: {selected.subject_b}</p>
                            )}
                        </div>
                        <button onClick={() => setSelectedId(null)} className="link-underline text-xs">Close</button>
                    </div>
                    {selected.ab_status && selected.ab_status !== "pending" && (
                        <div className="mt-4 border border-[color:var(--espresso)] p-4" data-testid="campaign-ab-status">
                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">A/B Subject Test</p>
                            {selected.ab_status === "testing" && (
                                <p className="text-sm mt-2 text-[color:var(--soft-cream)]">
                                    Test in progress · variants sent to {(selected.ab_variant_a_size || 0)} + {(selected.ab_variant_b_size || 0)} contacts.
                                    Winner picks and sends to {(selected.ab_holdout_ids?.length || 0)} holdout on{" "}
                                    <span className="text-[color:var(--gold)]">{selected.ab_pick_winner_at ? new Date(selected.ab_pick_winner_at).toLocaleString() : "…"}</span>.
                                </p>
                            )}
                            {selected.ab_status === "picking_winner" && (
                                <p className="text-sm mt-2 text-[color:var(--soft-cream)]">Picking the winner and dispatching to the holdout right now…</p>
                            )}
                            {selected.ab_status === "winner_sent" && (
                                <div className="mt-2 text-sm text-[color:var(--soft-cream)] space-y-1">
                                    <p>
                                        Winner: <strong className="text-[color:var(--gold)] uppercase">Variant {selected.ab_winner}</strong> —{" "}
                                        <em>"{selected.ab_winner === "a" ? selected.subject : selected.subject_b}"</em>
                                    </p>
                                    <p className="text-xs text-[color:var(--cream-muted)]">
                                        A: {selected.ab_variant_a_opens ?? 0} unique opens · B: {selected.ab_variant_b_opens ?? 0} unique opens · Winner sent to {selected.ab_holdout_sent ?? 0} holdout.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Send TEST to</label>
                            <div className="flex gap-2 mt-2">
                                <input className="input-line flex-1" placeholder="you@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-test-email" />
                                <button onClick={() => sendTest(selected.id)} className="btn-gold text-xs" data-testid="campaign-send-test">Send test</button>
                            </div>
                        </div>
                        <div>
                            <label className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Live send</label>
                            <div className="mt-2 space-y-2">
                                <button
                                    onClick={() => audience && sendReal(selected.id, audience)}
                                    disabled={!audience || selected.status !== "draft"}
                                    className="btn-gold btn-gold-solid text-xs disabled:opacity-40 w-full"
                                    data-testid="campaign-send-live"
                                >
                                    {selected.status === "draft"
                                        ? `Send now to ${audience ?? "…"} opted-in contacts`
                                        : selected.status === "sending" ? "Sending in background…"
                                        : selected.status === "scheduled" ? `Scheduled · ${formatScheduled(selected.scheduled_for)}`
                                        : "Sent"}
                                </button>
                                {(selected.status === "draft" || selected.status === "scheduled") && (
                                    <div className="border border-[color:var(--espresso)] p-3" data-testid="campaign-schedule-block">
                                        <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Or schedule for later</p>
                                        <input
                                            type="datetime-local"
                                            className="input-line w-full text-xs"
                                            value={scheduleAt}
                                            onChange={(e) => setScheduleAt(e.target.value)}
                                            data-testid="campaign-schedule-input"
                                        />
                                        <p className="text-[0.55rem] text-[color:var(--cream-muted)] mt-1">Your local time. Recommended: Thu 10am for best open rates.</p>
                                        <div className="mt-2 flex gap-2">
                                            <button
                                                onClick={() => audience && scheduleSend(selected.id, audience)}
                                                disabled={!audience || scheduling || !scheduleAt}
                                                className="btn-gold text-[0.65rem] flex-1 disabled:opacity-40"
                                                data-testid="campaign-schedule-btn"
                                            >
                                                {scheduling ? "Scheduling…" : selected.status === "scheduled" ? "Reschedule" : "Schedule send"}
                                            </button>
                                            {selected.status === "scheduled" && (
                                                <button
                                                    onClick={() => unschedule(selected.id)}
                                                    className="text-[0.65rem] px-3 py-1 border border-[color:var(--wine)] text-[color:var(--wine)] hover:bg-[color:var(--wine)] hover:text-[color:var(--soft-cream)]"
                                                    data-testid="campaign-unschedule-btn"
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <CampaignAnalytics campaignId={selected.id} />
                </div>
            )}

            {campaigns.length === 0 ? (
                <div className="border border-dashed border-[color:var(--espresso)] p-10 text-center" data-testid="campaigns-empty">
                    <p className="text-sm text-[color:var(--cream-muted)]">No campaigns yet. Import contacts, then click "New campaign" above.</p>
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-[color:var(--gold)] text-left">
                        <tr className="border-b border-[color:var(--espresso)]">
                            <th className="py-3">Campaign</th>
                            <th>Status</th>
                            <th>Sent</th>
                            <th>Failed</th>
                            <th className="text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns.map((c) => (
                            <tr key={c.id} className="border-b border-[color:var(--espresso)]/50" data-testid={`campaign-row-${c.id}`}>
                                <td className="py-3">
                                    <div className="font-serif text-lg text-[color:var(--soft-cream)]">{c.name}</div>
                                    <div className="text-xs text-[color:var(--cream-muted)]">{c.subject}</div>
                                </td>
                                <td className="text-xs uppercase tracking-[0.16em]">
                                    {c.status}
                                    {c.status === "scheduled" && c.scheduled_for && (
                                        <div className="normal-case text-[0.6rem] tracking-normal text-[color:var(--gold)] mt-1">{formatScheduled(c.scheduled_for)}</div>
                                    )}
                                </td>
                                <td>{c.stats?.sent ?? 0}</td>
                                <td>{c.stats?.failed ?? 0}</td>
                                <td className="text-right">
                                    <div className="inline-flex items-center gap-3">
                                        <button onClick={() => setSelectedId(c.id)} className="link-underline text-xs text-[color:var(--gold)]" data-testid={`campaign-open-${c.id}`}>Open</button>
                                        <button onClick={() => testToSelf(c.id, c.name)} className="link-underline text-xs text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]" title="Send a test copy to your own inbox" data-testid={`campaign-test-self-${c.id}`}>Test to me</button>
                                        <button onClick={() => duplicateCampaign(c.id)} className="link-underline text-xs text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]" title="Clone into a fresh draft" data-testid={`campaign-duplicate-${c.id}`}>Duplicate</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
